import { isSafeReturnPath } from "@shared-ui/utils/returnTo";

/**
 * セッション切れの再ログインで、元のテナント・元の画面へ戻す（PRODUCT_DEV-1129）
 *
 * プロダクト（Unizo）はセッションが切れてここへ出すとき、URL に `lastTenantId` と
 * `returnTo` を付ける。名前と意味の正本は `@shared-ui/utils/returnTo` の
 * `PORTAL_RESUME_PARAMS`（両側が同じ定数を読む）。
 *
 * - `lastTenantId` は**「入りたい」という希望**で、`tenantId`（所属をそのテナントだけに
 *   絞る）とは違う。所属していて、そのテナントで頼まれたプロダクトを使えるときだけ
 *   選択画面を飛ばす。それ以外は普通の選択画面を出す（共用 PC で別の人がログインした・
 *   テナントから外された・契約が外れた、で詰まらせないため）
 * - `returnTo` は、プロダクトへ戻すとき `#authCode=…&returnTo=…` で返す。
 *   検査はプロダクトと同じ `isSafeReturnPath`（規則を2か所に持たない）
 */

type TenantLike = { tenantId: string; products: readonly { id: string }[] };

/**
 * 選択画面を出さずに入るテナント。出すときは `null`。
 *
 * - 所属が1件ならその1件（従来どおり）
 * - テナントが既に決まっている（`?tenantId=` か配信ホスト）ときは、その絞り込みに任せる
 * - 直前のテナントに所属していて、頼まれたプロダクトをそこで使えればそのテナント
 */
export const pickTenantWithoutAsking = <T extends TenantLike>(
  tenants: readonly T[],
  options: {
    forcedTenantId: string | null;
    lastTenantId: string | null;
    productId: string | null;
  },
): T | null => {
  if (tenants.length === 1) return tenants[0];
  const { forcedTenantId, lastTenantId, productId } = options;
  if (forcedTenantId || !lastTenantId) return null;
  return (
    tenants.find(
      (t) =>
        t.tenantId === lastTenantId &&
        (!productId || t.products.some((p) => p.id === productId)),
    ) ?? null
  );
};

/**
 * プロダクトへ返す戻り先。返さないものは `null`。
 *
 * **直前のテナントと違うテナントで入ったら返さない。** 前に使っていた人・前のテナントの
 * 画面なので、開いても見られないか、別の人の画面になる。`lastTenantId` が無いとき
 * （共有された URL から初めてログインした等）は、どのテナントでも返す（PRODUCT_DEV-916 と同じ）。
 */
export const returnToForProduct = (args: {
  returnTo: string | null;
  lastTenantId: string | null;
  chosenTenantId: string;
}): string | null => {
  const { returnTo, lastTenantId, chosenTenantId } = args;
  if (!returnTo || !isSafeReturnPath(returnTo)) return null;
  if (lastTenantId && lastTenantId !== chosenTenantId) return null;
  return returnTo;
};
